import { useQuery } from '@tanstack/react-query';
import { Field, Group, NotificationRule, Site as SiteSchema, WebhookEndpoint, page } from '@cloudsign/shared';
import type { NotificationChannel, NotificationRuleCreate, Site } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';

export const RuleList = z.array(NotificationRule);
export const TriggerInfo = z.object({ key: z.string(), mergeFields: z.array(z.string()), recipients: z.array(z.string()) });
export type TriggerInfo = z.infer<typeof TriggerInfo>;
export const TriggerList = z.array(TriggerInfo);
export const EndpointList = z.array(WebhookEndpoint);
export const SitePage = page(SiteSchema);
export const GroupList = z.array(Group);
export const FieldList = z.array(Field);

export const rulesKey = ['notifications', 'rules'] as const;
export const endpointsKey = ['webhooks', 'endpoints'] as const;

export function useRules() {
  return useQuery({ queryKey: rulesKey, queryFn: () => api.get('/notifications/rules', { schema: RuleList }) });
}

export function useTriggers() {
  return useQuery({ queryKey: ['notifications', 'triggers'], queryFn: () => api.get('/notifications/triggers', { schema: TriggerList }), staleTime: 300_000 });
}

export function useSites() {
  return useQuery({
    queryKey: ['notifications', 'sites'],
    queryFn: () => api.get('/sites', { query: { status: 'active', pageSize: 200 }, schema: SitePage }).then((r): Site[] => r.items),
    staleTime: 60_000,
  });
}

export function useGroups() {
  return useQuery({ queryKey: ['notifications', 'groups'], queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }), staleTime: 60_000 });
}

export function useEndpoints() {
  return useQuery({ queryKey: endpointsKey, queryFn: () => api.get('/webhooks/endpoints', { schema: EndpointList }) });
}

/** Sign-in questions of one site (for rule filters). */
export function useSiteFields(siteId: string | null) {
  return useQuery({
    queryKey: ['notifications', 'fields', siteId],
    enabled: siteId !== null,
    queryFn: () => api.get(`/fields/sites/${siteId}`, { query: { scope: 'sign_in', status: 'active' }, schema: FieldList }),
  });
}

/** What the channel can send to. Push goes to people; SMS to phones; email to people and addresses; webhook to an endpoint. */
export function allowedRecipients(channel: NotificationChannel, triggerRecipients: readonly string[]): { host: boolean; member: boolean; reception: boolean; emails: boolean; phones: boolean; endpoint: boolean } {
  const has = (k: string) => triggerRecipients.includes(k);
  return {
    host: has('host') && channel !== 'webhook',
    member: has('member') && channel !== 'webhook',
    reception: has('reception') && (channel === 'email' || channel === 'sms'),
    emails: has('emails') && channel === 'email',
    phones: has('phones') && channel === 'sms',
    endpoint: has('endpoint') && channel === 'webhook',
  };
}

export function emptyRule(trigger: NotificationRuleCreate['trigger'] = 'visit.signed_in'): NotificationRuleCreate {
  return {
    name: '',
    enabled: true,
    trigger,
    siteIds: null,
    groupIds: null,
    filters: [],
    channel: 'email',
    recipients: { host: false, member: false, reception: true, emails: [], phones: [], endpointId: null, connectionId: null },
    subject: '',
    body: '',
  };
}
